import React, { useRef } from "react";
import PropTypes from "prop-types";
import cx from "clsx";
import { InputLabel } from "../../../../../../shared/InputLabel/InputLabel";
import { InputError } from "../../../../../../shared/InputError/InputError";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import styles from "../styles.module.scss";
import { Modal } from "../../../../../../shared/Modal/Modal";
import { Button } from "../../../../../../shared/Button/Button";
import { CircularProgress } from "../../../../../../shared/CircularProgress/CircularProgress";
import { ChevronLeftIcon } from "../../../../../../assets/Icons/ChevronLeftIcon";
import { useClientQuery } from "../../../../../../api/queries/useClientQuery";
import { TextEditor } from "../../../../../../shared/TextEditor/TextEditor";
import { PlusIcon } from "../../../../../../assets/Icons/PlusIcon";
import { DeleteIcon } from "../../../../../../assets/Icons/DeleteIcon";
import { uiNotification } from "../../../../../../services/UINotificationService";

const TemplateForm = ({
  content,
  onClose,
  submit,
  isLoading,
  clientId,
  setShowTemplateForm,
  form,
}) => {
  const { tInbox } = useAppTranslation.Inbox();
  const fileInputRef = useRef(null);

  const { data: client } = useClientQuery({
    clientId: parseInt(clientId || form.patient.value?.value),
  });

  const ALLOWED_FILE_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];
  const MAX_FILES = 25;
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB in bytes

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const currentAttachments = form.attachments.value || [];

    // Check max files limit
    if (currentAttachments.length + files.length > MAX_FILES) {
      uiNotification.error(
        `You can only attach a maximum of ${MAX_FILES} files.`,
      );
      event.target.value = "";
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(
      (file) => !ALLOWED_FILE_TYPES.includes(file.type),
    );
    if (invalidFiles.length > 0) {
      uiNotification.error(
        "Only PDF, DOC, DOCX, JPG, JPEG, and PNG files are allowed.",
      );
      event.target.value = "";
      return;
    }

    // Calculate total size
    const newTotalSize = [...currentAttachments, ...files].reduce(
      (sum, file) => sum + file.size,
      0,
    );
    if (newTotalSize > MAX_TOTAL_SIZE) {
      uiNotification.error("Total file size cannot exceed 100MB.");
      event.target.value = "";
      return;
    }

    // Add files
    form.attachments.onChange([...currentAttachments, ...files]);
    event.target.value = "";
  };

  const handleRemoveFile = (indexToRemove) => {
    const currentAttachments = form.attachments.value || [];
    form.attachments.onChange(
      currentAttachments.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleAddFileClick = () => {
    if (form.attachments.value.length >= MAX_FILES) {
      uiNotification.error(
        `You can only attach a maximum of ${MAX_FILES} files.`,
      );
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <Modal
      isOpen
      footerNoBorder
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
      footerClassName={cx("flex", "justify-end")}
      header={
        <div className="flex items-center">
          <Button
            variant="text"
            className="m-r-10"
            size="small"
            onClick={() => setShowTemplateForm(false)}
          >
            <ChevronLeftIcon />
          </Button>
          <Modal.Title>{`${tInbox("efaxOrders.modalAdd.title")} ${
            client ? `for ${client.fullName}` : ""
          }`}</Modal.Title>
        </div>
      }
      footer={
        <Button
          onClick={() => submit()}
          isDisabled={isLoading}
          leftAdornment={
            isLoading ? (
              <CircularProgress size="small" color="white" />
            ) : undefined
          }
        >
          {tInbox("efaxOrders.modalAdd.submit")}
        </Button>
      }
      contentClassName={styles.root}
    >
      <div className="flex-col">
        <InputLabel>{tInbox("efaxOrders.modalAdd.clinic")}</InputLabel>
        <span className="bold fontSize14px">{form.clinic.value?.label}</span>
      </div>
      <div className="flex-col">
        <InputLabel>{tInbox("efaxOrders.modalAdd.user")}</InputLabel>
        <span className="bold fontSize14px">{form.user.value?.label}</span>
      </div>
      <div className="flex-col">
        <InputLabel>{tInbox("efaxOrders.modalAdd.toNumber")}</InputLabel>
        <span className="bold fontSize14px">{form.favorites.value?.label}</span>
        <span className={styles.favoritesLabel}>
          {form.favorites.value?.faxNumber}
        </span>
      </div>
      <div className="flex items-center justify-between m-b-10">
        <InputLabel>{tInbox("efaxOrders.modalAdd.labForm")}</InputLabel>
        <Button
          variant="text"
          size="small"
          onClick={handleAddFileClick}
          className="flex items-center"
        >
          <PlusIcon />
          <span className="m-l-5">Attach File</span>
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        multiple
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {form.attachments.value && form.attachments.value.length > 0 && (
        <div className="m-b-10">
          {form.attachments.value.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between p-10 m-b-5"
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <span className="fontSize14px">{file.name}</span>
              <Button
                variant="text"
                size="small"
                onClick={() => handleRemoveFile(index)}
              >
                <DeleteIcon />
              </Button>
            </div>
          ))}
          <div className="fontSize12px" style={{ color: "#666" }}>
            {form.attachments.value.length} / {MAX_FILES} files (
            {(
              form.attachments.value.reduce((sum, file) => sum + file.size, 0) /
              1024 /
              1024
            ).toFixed(2)}{" "}
            MB / 100 MB)
          </div>
        </div>
      )}

      <TextEditor
        onChange={(value) => content.onChange(value)}
        value={content.value.value}
        className={styles.textarea}
        init={{
          content_style: "p, h1, h2, h3, h4, h5, h6 { margin: 0; }",
        }}
      />
      {Boolean(content.value.error) && (
        <InputError>{content.value.error}</InputError>
      )}
    </Modal>
  );
};

TemplateForm.propTypes = {
  content: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    value: PropTypes.shape({
      value: PropTypes.string,
      error: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  clientId: PropTypes.string,
  setShowTemplateForm: PropTypes.func.isRequired,
  form: PropTypes.shape({
    clinic: PropTypes.shape({
      value: PropTypes.shape({
        label: PropTypes.string,
      }),
    }).isRequired,
    user: PropTypes.shape({
      value: PropTypes.shape({
        label: PropTypes.string,
      }),
    }).isRequired,
    favorites: PropTypes.shape({
      value: PropTypes.shape({
        label: PropTypes.string,
        faxNumber: PropTypes.string,
      }),
    }).isRequired,
    patient: PropTypes.shape({
      value: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
    attachments: PropTypes.shape({
      value: PropTypes.arrayOf(PropTypes.instanceOf(File)),
      onChange: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
};

export default TemplateForm;
